import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call } from './client';
import { qk } from './keys';
import type { Notification } from './types';

export type InboxFilter = 'all' | 'action' | 'unread' | 'archived';

function filterQuery(filter: InboxFilter) {
  return {
    archived: filter === 'archived' ? true : undefined,
    needsAction: filter === 'action' ? true : undefined,
    unreadOnly: filter === 'unread' ? true : undefined,
  };
}

export function useInbox(filter: InboxFilter) {
  return useInfiniteQuery({
    queryKey: qk.inbox({ filter }),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      call(api.GET('/api/v1/inbox', { params: { query: { ...filterQuery(filter), limit: 50, cursor: pageParam } } })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 15_000,
  });
}

/** Лічильник непрочитаних для навігації. */
export function useUnreadCount() {
  return useQuery({
    queryKey: qk.inbox({ count: true }),
    queryFn: async () => (await call(api.GET('/api/v1/inbox', { params: { query: { limit: 1 } } }))).unreadCount,
    staleTime: 30_000,
    refetchInterval: 120_000,
  });
}

export function useInboxAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'read' | 'archive' }) =>
      action === 'read'
        ? call(api.POST('/api/v1/inbox/{id}/read', { params: { path: { id } } }))
        : call(api.POST('/api/v1/inbox/{id}/archive', { params: { path: { id } } })),
    onSuccess: (n: Notification) => {
      qc.setQueriesData<{ pages: { data: Notification[] }[] }>({ queryKey: qk.inboxAll }, (d) =>
        d && Array.isArray(d.pages) ? { ...d, pages: d.pages.map((p) => ({ ...p, data: p.data.map((x) => (x.id === n.id ? n : x)) })) } : d,
      );
      void qc.invalidateQueries({ queryKey: qk.inboxAll });
    },
  });
}

export function useReadAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => call(api.POST('/api/v1/inbox/read-all')),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.inboxAll }),
  });
}

export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  'task.assigned': 'Призначення',
  'comment.mention': 'Згадка',
  'comment.created': 'Коментар',
  'task.completed': 'Завершення',
  'approval.decided': 'Погодження',
  'approval.requested': 'Погодження',
};

/** Сповіщення модулів (legal.*, mgmt.*, time.* …) — назвою розділу, звідки воно прийшло, а не безликим «Сповіщення». */
const MODULE_NOTIFICATION_LABELS: Record<string, string> = {
  legal: 'Юридичний контур',
  mgmt: 'Управлінський контур',
  time: 'Облік часу',
  meetings: 'Зустрічі',
  meeting: 'Зустрічі',
  people: 'Персонал',
  finance: 'Фінанси робіт',
  report: 'Звіти',
  reports: 'Звіти',
  metric: 'Показники',
  kb: 'Процеси й знання',
  content: 'Контент',
  ai: 'ШІ-помічники',
  automation: 'Автоматизація',
  connectors: 'Інтеграції',
  admin: 'Адміністрування',
  migration: 'Перенесення даних',
  task: 'Задача',
  comment: 'Коментар',
  approval: 'Погодження',
};

/** Підпис типу сповіщення у вхідних: точний тип ядра або розділ модуля за префіксом типу. */
export function notificationTypeLabel(type: string): string {
  return NOTIFICATION_TYPE_LABELS[type] ?? MODULE_NOTIFICATION_LABELS[type.split('.')[0] ?? ''] ?? 'Сповіщення';
}
